import { useCallback, useEffect } from 'react';

import { batch } from 'react-redux';

import { useEcho } from '@laravel/echo-react';
import { useAppDispatch, useAppSelector } from 'redux/store/store.types';

import { useLazyFetchChatsQuery, useLazyFetchMessagesQuery } from '../api/app-chat.api';
import {
  addChat,
  addMessages,
  addOneMessage,
  clearState,
  removeChat,
  setChats,
  setEventTrigger,
  setHasMoreMessages,
} from '../store/app-chat.slice';
import { ChatEventPayload, EChatEventType, FetchMessagesPayload } from '../types/app-chat.type';
import { useChatMessages } from './app-chat-message.hook';

export const useChatEvents = () => {
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.authSession);
  const { onChatPage, activeChatId } = useAppSelector((state) => state.appChat);

  const { markAsRead } = useChatMessages();

  const [fetchChatsQuery] = useLazyFetchChatsQuery();
  const [fetchMessagesQuery] = useLazyFetchMessagesQuery();

  const fetchMessages = async (payload: FetchMessagesPayload) => {
    const { data: response, error } = await fetchMessagesQuery(payload);
    const isError = error || !response?.data;
    if (isError) return;
    batch(() => {
      dispatch(addMessages({ chatId: payload.chat_id, messages: response.data }));
      dispatch(setHasMoreMessages({ chatId: payload.chat_id, hasMore: response.meta.has_more }));
    });
  };

  const fetchChats = useCallback(async () => {
    if (currentUser) {
      const { data: response, error } = await fetchChatsQuery();
      const isError = error || !response?.data;
      if (isError) return;

      dispatch(setChats(response.data));
      response.data.forEach((chat) => {
        fetchMessages({ chat_id: chat.id, limit: 15, direction: 'latest' });
      });
    }
  }, [currentUser]);

  const clearChatState = () => {
    dispatch(clearState());
  };

  const chatEventHandler = useCallback(
    ({ payload }: ChatEventPayload) => {
      if (!currentUser) return;
      const { message, chat, event } = payload;
      if (event === EChatEventType.NEW_MESSAGE && message) {
        if (message.sender_id === currentUser.id) return;

        batch(() => {
          dispatch(addOneMessage(message));
          if (!onChatPage) {
            dispatch(setEventTrigger({ type: EChatEventType.NEW_MESSAGE, message }));
          }
        });
      }
      if (event === EChatEventType.JOIN_CHANNEL && chat) {
        dispatch(addChat(chat));
        fetchMessages({ chat_id: chat.id, limit: 15, direction: 'latest' });
      }
      if (event === EChatEventType.LEAVE_CHANNEL && chat) {
        dispatch(removeChat(chat));
      }
    },
    [currentUser],
  );

  const { leave: leaveChat } = useEcho<ChatEventPayload>(
    `App.Chat.${currentUser?.id}`,
    Object.values(EChatEventType),
    (data) => {
      chatEventHandler(data);
    },
  );

  useEffect(() => {
    markAsRead();
  }, [activeChatId]);

  useEffect(() => {
    fetchChats();
    return () => {
      leaveChat();
      clearChatState();
    };
  }, []);
};
